import React, { useCallback, useEffect, useState } from "react";
import { Collapse, Spin, Modal, Input, Pagination, Button,Empty } from "antd";
import { toast } from "react-toastify";
import { inputFieldCharLimit } from "../../config";
import {
  getPendingMicrosites,
  groupPendingMicrositesByOrganization,
  reviewMicrosite,
} from "../../api/micrositeApprovalsApi";
import "./table.scss";

const DEFAULT_PAGE_SIZE = 15;
const ORG_LIST_PAGE_SIZE = 10;

const MicrositeApprovalsTab = ({
  partnerScope,
  isActive = true,
  onReviewSuccess,
}) => {
  const [organisations, setOrganisations] = useState([]);
  const [orgListPage, setOrgListPage] = useState(0);
  const [orgListTotal, setOrgListTotal] = useState(0);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [activeOrgKey, setActiveOrgKey] = useState(null);
  const [micrositesByOrg, setMicrositesByOrg] = useState({});
  const [allGroupedOrgs, setAllGroupedOrgs] = useState([]);
  const [micrositesListPage, setMicrositesListPage] = useState(0);
  const [micrositesListTotal, setMicrositesListTotal] = useState(0);
  const [isDenyModalOpen, setIsDenyModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [selectedMicrositeId, setSelectedMicrositeId] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [reasonValue, setReasonValue] = useState("");
  const [isBtnLoading, setIsBtnLoading] = useState(false);

  const applyOrgListPage = useCallback((groupedOrgs, pageNumber) => {
    const start = pageNumber * ORG_LIST_PAGE_SIZE;
    const pageOrgs = groupedOrgs.slice(start, start + ORG_LIST_PAGE_SIZE);
    setOrganisations(pageOrgs);
    setOrgListPage(pageNumber);
    setOrgListTotal(groupedOrgs.length);

    const sitesMap = {};
    pageOrgs.forEach((org) => {
      sitesMap[org.id] = org.microsites || [];
    });
    setMicrositesByOrg(sitesMap);
    setActiveOrgKey(pageOrgs[0]?.id || null);
  }, []);

  const loadPendingMicrosites = useCallback(
    async (apiPageNumber = 0) => {
      try {
        setLoadingOrgs(true);
        const data = await getPendingMicrosites(
          apiPageNumber,
          DEFAULT_PAGE_SIZE,
          partnerScope
        );
        const { organisations: grouped, micrositesByOrg: sitesByOrg } =
          groupPendingMicrositesByOrganization(data?.result || []);

        const groupedWithSites = grouped.map((org) => ({
          ...org,
          microsites: sitesByOrg[org.id] || [],
        }));

        setAllGroupedOrgs(groupedWithSites);
        setMicrositesListPage(data?.pageNumber ?? apiPageNumber);
        setMicrositesListTotal(data?.totalCount ?? groupedWithSites.length);
        applyOrgListPage(groupedWithSites, 0);
      } catch (error) {
        toast.error(
          error?.error || error?.message || "Failed to load site approvals"
        );
        setOrganisations([]);
        setAllGroupedOrgs([]);
        setMicrositesByOrg({});
        setOrgListTotal(0);
        setMicrositesListTotal(0);
      } finally {
        setLoadingOrgs(false);
      }
    },
    [partnerScope, applyOrgListPage]
  );

  useEffect(() => {
    if (!isActive) return;
    loadPendingMicrosites(0);
  }, [isActive, loadPendingMicrosites]);

  const handleOrgListPageChange = (page) => {
    applyOrgListPage(allGroupedOrgs, page - 1);
  };

  const handleMicrositesListPageChange = (page) => {
    loadPendingMicrosites(page - 1);
  };

  const handleAccordionChange = (key) => {
    const organisationId = Array.isArray(key) ? key[0] : key;
    setActiveOrgKey(organisationId || null);
  };

  const showDenyModal = (micrositeId, organisationId) => {
    setSelectedMicrositeId(micrositeId);
    setSelectedOrgId(organisationId);
    setReasonValue("");
    setIsDenyModalOpen(true);
  };

  const showApproveModal = (micrositeId, organisationId) => {
    setSelectedMicrositeId(micrositeId);
    setSelectedOrgId(organisationId);
    setIsApproveModalOpen(true);
  };

  const handleCancel = () => {
    setIsDenyModalOpen(false);
    setIsApproveModalOpen(false);
    setSelectedMicrositeId(null);
    setSelectedOrgId(null);
    setReasonValue("");
  };

  const removeMicrositeFromState = (micrositeId, organisationId) => {
    const next = allGroupedOrgs
      .map((org) => {
        if (org.id !== organisationId) return org;
        const microsites = (org.microsites || []).filter(
          (m) => m.id !== micrositeId
        );
        return {
          ...org,
          microsites,
          pendingMicrositeCount: microsites.length,
        };
      })
      .filter((org) => (org.microsites || []).length > 0);

    const maxPage = Math.max(
      0,
      Math.ceil(next.length / ORG_LIST_PAGE_SIZE) - 1
    );
    const safePage = Math.min(orgListPage, maxPage);

    setAllGroupedOrgs(next);
    setMicrositesListTotal((prev) => Math.max(0, prev - 1));
    applyOrgListPage(next, safePage);
  };

  const approveOrDeniedData = async (action) => {
    setIsBtnLoading(true);
    try {
      if (action === "REJECT" && !reasonValue.trim()) {
        toast.error("Please provide a rejection reason.");
        setIsBtnLoading(false);
        return;
      }

      const response = await reviewMicrosite(selectedMicrositeId, {
        action,
        rejectionReason: reasonValue ? reasonValue : "",
      });

      if (response) {
        toast.success(
          action === "REJECT" ? "Denied successfully!" : "Approved successfully!"
        );
        handleCancel();
        if (selectedOrgId) {
          removeMicrositeFromState(selectedMicrositeId, selectedOrgId);
        }
        await onReviewSuccess?.();
      }
    } catch (error) {
      toast.error(error?.error || error?.message || "Action failed");
    } finally {
      setIsBtnLoading(false);
    }
  };

  const getStatusClass = (status) => {
    const normalized = (status || "").toLowerCase();
    if (normalized === "approved") return "ms-status-badge ms-status-approved";
    if (normalized === "denied" || normalized === "rejected") {
      return "ms-status-badge ms-status-denied";
    }
    return "ms-status-badge ms-status-pending";
  };

  const renderSiteCards = (organisationId) => {
    const sites = micrositesByOrg[organisationId] || [];

    if (!sites.length) {
      return (
        <div className="microsite-site-empty">
          <Empty description="No Data" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      );
    }

    return (
      <ul className="microsite-site-list">
        {sites.map((row) => (
          <li key={row.id} className="microsite-site-card">
            <div className="microsite-site-card__info">
              <span className="microsite-site-card__name" title={row.name}>
                {row.name}
              </span>
            </div>
            <div className="microsite-site-card__status">
              <span className={getStatusClass(row.approvalStatus)}>
                {row.approvalStatus || "PENDING"}
              </span>
            </div>
            <div className="microsite-site-card__actions">
              <Button
                className="table-deny-btn microsite-site-card__btn"
                onClick={() => showDenyModal(row.id, organisationId)}
              >
                Deny
              </Button>
              <Button
                className="table-approve-btn microsite-site-card__btn"
                onClick={() => showApproveModal(row.id, organisationId)}
              >
                Approve
              </Button>
            </div>
          </li>
        ))}
      </ul>
    );
  };

  if (loadingOrgs) {
    return (
      <div className="data-loader">
        <Spin className="spin" tip="Loading" size="large" />
      </div>
    );
  }

  if (!organisations.length) {
    return (
      <div className="microsite-approvals-empty">
        <Empty description="No Data"  image={Empty.PRESENTED_IMAGE_SIMPLE}/>
      </div>
    );
  }

  return (
    <div className="microsite-approvals-tab">
      <div className="microsite-org-columns-head" role="row">
        <span className="microsite-org-columns-head__icon-spacer" aria-hidden="true" />
        <div className="microsite-org-header microsite-org-columns-head__labels" role="presentation">
          <span className="microsite-org-header__cell" role="columnheader">
            Organisation Name
          </span>
          <span className="microsite-org-header__cell" role="columnheader">
            Address
          </span>
          <span className="microsite-org-header__cell" role="columnheader">
            Organisation Website
          </span>
          <span className="microsite-org-header__cell" role="columnheader">
            Phone number
          </span>
          <span className="microsite-org-header__cell" role="columnheader">
            Email
          </span>
        </div>
      </div>
      <Collapse
        accordion
        activeKey={activeOrgKey || undefined}
        onChange={handleAccordionChange}
        className="microsite-org-accordion"
        expandIconPosition="start"
      >
        {organisations.map((org) => (
          <Collapse.Panel
            header={
              <div className="microsite-org-header">
                <span className="microsite-org-header__cell microsite-org-name" title={org.name || ""}>
                  {org.name}
                </span>
                <span className="microsite-org-header__cell" title={org.address || ""}>
                  {org.address}
                </span>
                <span className="microsite-org-header__cell">
                  {org.websiteLink ? (
                    <a
                      className="microsite-org-header__link"
                      href={`//${org.websiteLink}`}
                      target="_blank"
                      rel="noreferrer"
                      title={org.websiteLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {org.websiteLink}
                    </a>
                  ) : null}
                </span>
                <span className="microsite-org-header__cell" title={org.phone || ""}>
                  {org.phone}
                </span>
                <span className="microsite-org-header__cell" title={org.email || ""}>
                  {org.email}
                </span>
              </div>
            }
            key={org.id}
          >
            <div className="microsite-approvals-sites">
              {renderSiteCards(org.id)}
            </div>
          </Collapse.Panel>
        ))}
      </Collapse>
      {orgListTotal > ORG_LIST_PAGE_SIZE && (
        <Pagination
          className="pagination microsite-org-list-pagination"
          current={orgListPage + 1}
          pageSize={ORG_LIST_PAGE_SIZE}
          total={orgListTotal}
          showSizeChanger={false}
          showTotal={(total) => `${total} organisations`}
          onChange={handleOrgListPageChange}
          style={{ marginBottom: 16 }}
        />
      )}
      {micrositesListTotal > DEFAULT_PAGE_SIZE && (
        <Pagination
          className="pagination microsite-sites-pagination"
          current={micrositesListPage + 1}
          pageSize={DEFAULT_PAGE_SIZE}
          total={micrositesListTotal}
          showSizeChanger={false}
          showTotal={(total) => `Pending approval for ${total} Sites`}
          onChange={handleMicrositesListPageChange}
          style={{ marginBottom: 16 }}
        />
      )}

      <Modal
        className="confirmation-modal"
        open={isDenyModalOpen}
        onCancel={handleCancel}
        destroyOnClose={true}
        closable={false}
        footer={null}
      >
        <div className="message">
          <p>Are you sure you want to Deny?</p>
        </div>
        <div className="deny-reason">
          <Input.TextArea
            placeholder="Reason"
            value={reasonValue || ""}
            onChange={(e) => setReasonValue(e.target.value)}
            maxLength={inputFieldCharLimit}
            rows={4}
          />
        </div>
        <div className="modalBtns">
          <Button className="cancel-btn" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            className="confirm-btn"
            onClick={() => approveOrDeniedData("REJECT")}
            loading={isBtnLoading}
          >
            Deny
          </Button>
        </div>
      </Modal>

      <Modal
        className="confirmation-modal"
        open={isApproveModalOpen}
        onCancel={handleCancel}
        destroyOnClose={true}
        closable={false}
        footer={null}
      >
        <div className="message">
          <p>Are you sure you want to Approve?</p>
        </div>
        <div className="modalBtns">
          <Button className="cancel-btn" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            className="confirm-btn"
            onClick={() => approveOrDeniedData("APPROVE")}
            loading={isBtnLoading}
          >
            Approve
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default MicrositeApprovalsTab;
